import { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  setIsEditing,
  useUpdateTransportMutation,
  type EtlTransportResponse,
  type EtlApiTransportUpdateRequest,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import {
  trackSubmitAirAmbulanceRequest,
  trackSubmitAirAmbulanceRequestApiResponse,
} from '../../../../gainsightTrackings/airAmbulanceTrackings';
import {
  getErrorMessage,
  getErrorStatusCode,
} from '../../AirAmbulanceForm/utils';
import { parseCoordinates } from '../../AirAmbulanceForm/validation';
import type { EditFormValues } from './useEditFormValues';

/**
 * Handles the submission of the Air Ambulance ETL Edit Form, including API interaction and tracking.
 * @param transport - The current transport data being edited
 * @returns An object containing the submit handler, cancel handler, and loading state
 */
export function useEditFormSubmit(transport: EtlTransportResponse): {
  handleSubmit: (values: EditFormValues) => Promise<void>;
  handleCancel: () => void;
  isLoading: boolean;
} {
  const dispatch = useDispatch();
  const [updateTransport] = useUpdateTransportMutation();
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (values: EditFormValues) => {
    if (isLoading) return;
    setIsLoading(true);

    trackSubmitAirAmbulanceRequest({
      requestId: transport.gmr_transport_id,
      requestType: values.launch_type,
      uuid: transport.gmr_transport_id,
      isEdit: true,
    });

    try {
      const coords = parseCoordinates(values.pickup_coordinates);
      if (!coords || !coords.latitude || !coords.longitude) {
        throw new Error('Invalid coordinates');
      }
      const payload: EtlApiTransportUpdateRequest = {
        latitude: coords.latitude,
        longitude: coords.longitude,
        landingZoneId: values.landing_zone_external_id || null,
        pickupLocationComments: values.pickup_details || undefined,
        multipleUnites: values.multiple_units_requested === 'Yes' || undefined,
        otherResponders: values.air_service_responding === 'Yes',
        otherRespondersDeclinedWeather: values.air_service_declined === 'Yes',
        groundContact: values.ground_contact || undefined,
        callerAddress: values.pickup_location || undefined,
      };

      await updateTransport({
        transportId: transport.id,
        body: payload,
      }).unwrap();

      trackSubmitAirAmbulanceRequestApiResponse({
        requestId: transport.gmr_transport_id,
        status: 200,
        error: false,
        success: true,
        UUID: transport.gmr_transport_id,
        isEdit: true,
      });

      dispatch(setIsEditing(false));
    } catch (error) {
      trackSubmitAirAmbulanceRequestApiResponse({
        requestId: transport.gmr_transport_id,
        status: getErrorStatusCode(error),
        error: getErrorMessage(error),
        success: false,
        UUID: transport.gmr_transport_id,
        isEdit: true,
      });

      // eslint-disable-next-line no-console -- track errors in sentry
      console.error('Air Ambulance ETL Edit Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    dispatch(setIsEditing(false));
  };

  return { handleSubmit, handleCancel, isLoading };
}
